import "grapesjs/dist/css/grapes.min.css";

import React, { useEffect, useMemo, useRef } from "react";
import grapesjs from "grapesjs";
import type { Editor } from "grapesjs";
import blocksBasic from "grapesjs-blocks-basic";
import forms from "grapesjs-plugin-forms";
import flexboxBlocks from "grapesjs-blocks-flexbox";

import type { MarksheetTemplateProjectData } from "services/marksheetTemplateBuilderService";
import { extractCssFromProjectData, getBlankProjectData } from "./templates";
import { registerMarksheetBlocks } from "./blocks";

type Props = {
  editorKey: string;
  initialData: MarksheetTemplateProjectData | null;
  onEditorReady?: (editor: Editor) => void;
  onAutoSave?: (data: MarksheetTemplateProjectData) => void;
};

export default function MarksheetTemplateBuilderEditor({
  editorKey,
  initialData,
  onEditorReady,
  onAutoSave,
}: Props) {
  const containerId = useMemo(() => `gjs-ms-tpl-${editorKey}`, [editorKey]);
  const initialDataRef = useRef(initialData);
  const onAutoSaveRef = useRef(onAutoSave);
  onAutoSaveRef.current = onAutoSave;

  useEffect(() => {
    const container = document.getElementById(containerId);
    if (!container) return;

    container.innerHTML = "";

    const storageType = `msTpl:${editorKey}`;

    const normalizePlugin = (p: any) => (typeof p === "function" ? p : p?.default);
    const basicPlugin = normalizePlugin(blocksBasic);
    const formsPlugin = normalizePlugin(forms);
    const flexboxPlugin = normalizePlugin(flexboxBlocks);

    const editor = grapesjs.init({
      container: `#${containerId}`,
      height: "100%",
      width: "auto",
      fromElement: false,
      plugins: [basicPlugin, formsPlugin, flexboxPlugin].filter(Boolean) as any,
      storageManager: {
        type: storageType,
        autosave: true,
        autoload: true,
        stepsBeforeSave: 1,
      },
      canvas: {
        styles: [
          "body { background: #f2f4f8; }",
          ".ms-page { box-shadow: 0 10px 30px rgba(17,24,39,0.12); }",
        ],
      },
    });

    editor.Storage.add(storageType, {
      load: async () => {
        return (initialDataRef.current ?? getBlankProjectData()) as any;
      },
      store: async (data: MarksheetTemplateProjectData) => {
        onAutoSaveRef.current?.(data);
      },
    });

    registerMarksheetBlocks(editor);

    editor.on("load", () => {
      const css = extractCssFromProjectData(editor.getProjectData() as any);
      if (css) editor.setStyle(css);

      try {
        const panel = editor.Panels.getPanel("options");
        const buttons = panel?.get("buttons");
        if (buttons) {
          const sw = buttons.filter((b: any) => b?.get?.("id") === "sw-visibility")?.[0];
          const swIndex = sw ? buttons.indexOf(sw) : -1;
          const at = swIndex >= 0 ? swIndex : buttons.length;

          const mkBtn = (id: string, command: string, title: string, iconClass: string) =>
            ({ id, command, label: "", attributes: { title }, className: iconClass }) as any;

          if (!buttons.get("core-undo")) {
            buttons.add(mkBtn("core-undo", "core:undo", "Undo", "fa fa-undo"), { at });
          }
          if (!buttons.get("core-redo")) {
            buttons.add(mkBtn("core-redo", "core:redo", "Redo", "fa fa-repeat"), {
              at: swIndex >= 0 ? at + 1 : buttons.length,
            });
          }
        }
      } catch {}

      try {
        const cv = document.querySelector(`#${containerId} .gjs-cv-canvas`) as HTMLElement | null;
        if (cv) {
          cv.style.height = "100%";
          cv.style.overflowY = "auto";
          cv.style.overflowX = "hidden";
          cv.style.background = "#eef2f7";
          cv.style.padding = "12px 0";
        }
      } catch {}

      onEditorReady?.(editor);
    });

    editor.on("storage:store", (e: any) => {
      try {
        const data = e?.data;
        if (data && typeof data === "object") {
          (data as any)._msCss = editor.getCss();
          (data as any)._msHtml = editor.getHtml();
        }
      } catch {}
    });

    return () => {
      editor.destroy();
    };
  }, [containerId]);

  return <div id={containerId} style={{ height: "100%", minHeight: 0, flex: 1 }} />;
}
